import moment from "moment";

// Re-deriving event odometers from the vehicle's GPS activity.
//
// The driver's app carries the previous event's odometer forward rather than
// reading one per event, so a closer who corrects an event's time is still left
// holding the driver's stale mileage. These helpers pull the whole odometer
// column back onto the GPS track for the times the closer has actually entered.
//
// Two GPS sources, in preference order:
//
//   1. Pings (`telemetry`) — lytx.GPSLog joined to lytx.GPSLogDetails, one
//      odometer reading per ping at roughly 30-second cadence. Reading the last
//      ping at or before the event gives the odometer essentially exactly, with
//      no assumptions. This matches how OrderApi's AutoClosePreviewRepo sources
//      odometers, so the two can't drift apart.
//   2. Segments (`activity`) — the same GPSOdometerMiles column, but sampled
//      only at each segment's first and last ping. Used when pings are absent
//      (older payloads, or a gap in the ping stream around the event), which
//      means interpolating across the segment.
//
// Kept free of React/Redux so the matching maths can be unit tested directly.

export interface GpsSegment {
  startDateUTC?: string;
  endDateUTC?: string;
  activityState?: string;
  gpsOdometerMilesStart?: number | string | null;
  gpsOdometerMilesEnd?: number | string | null;
}

export interface GpsPing {
  occurrenceDT?: string;
  gpsOdometerMiles?: number | string | null;
}

export interface ApplyGpsOdometersResult {
  events: any[];
  /** Events whose odometer changed. */
  updatedCount: number;
  /** Active events skipped because they had no time or no GPS to match against. */
  unmatchedCount: number;
  /** Events sourced from an exact GPS ping. */
  fromPingCount: number;
  /** Events that fell back to interpolating across a segment. */
  interpolatedCount: number;
}

/**
 * How stale a ping may be and still be trusted as "the odometer at this time".
 * Cadence is ~30s, so this only bites across a real gap in the ping stream —
 * where interpolating between segment endpoints is the better estimate.
 */
export const MAX_PING_AGE_MS = 5 * 60 * 1000;

/**
 * The odometer column is whole miles, truncated — matching the legacy Dashboard's
 * VB Fix() behaviour, which is what the grid's edit cell, value formatter and
 * mobile card all apply to manually entered values. GPS-derived values go through
 * the same rule so a closer can't tell by looking which way a number arrived.
 */
export const truncateOdometer = (miles: number): number => Math.trunc(miles);

// Segment timestamps and event times are both UTC, but the API serialises them
// without a trailing Z. moment.utc reads both forms as UTC; new Date() would
// treat the un-suffixed ones as browser-local and match the wrong segment — an
// hours-wide error that produces silently wrong odometers rather than an
// obvious failure.
export const toUtcMs = (value: any): number => moment.utc(value).valueOf();

const toNumber = (value: any): number | null =>
  value === null || value === undefined || value === "" || isNaN(Number(value)) ? null : Number(value);

/** Drops segments we can't place on the timeline and puts the rest in time order. */
export const prepareGpsSegments = (segments: GpsSegment[] | undefined | null): GpsSegment[] =>
  (segments || [])
    .filter(
      (s) =>
        s?.startDateUTC &&
        s?.endDateUTC &&
        !isNaN(toUtcMs(s.startDateUTC)) &&
        !isNaN(toUtcMs(s.endDateUTC)) &&
        (toNumber(s.gpsOdometerMilesStart) !== null || toNumber(s.gpsOdometerMilesEnd) !== null)
    )
    .slice()
    .sort((a, b) => toUtcMs(a.startDateUTC) - toUtcMs(b.startDateUTC));

/**
 * Odometer on the GPS track at a point in time.
 *
 * Segments alternate Stopped/Moving and carry a start and end odometer, so an
 * event inside a Moving segment is interpolated by elapsed time rather than
 * snapped to wherever the vehicle was when that segment began. Stopped segments
 * have start == end, so interpolation is a no-op there.
 *
 * Outside the GPS run — before the first segment, inside a gap, or after the
 * last — the vehicle can't have moved on data we don't have, so the last known
 * odometer is held rather than extrapolated.
 *
 * @param segments must already be through prepareGpsSegments
 */
export const gpsOdometerAt = (segments: GpsSegment[], timeMs: number): number | null => {
  if (!segments.length || isNaN(timeMs)) return null;

  let previousEnd: number | null = null;

  for (const segment of segments) {
    const start = toUtcMs(segment.startDateUTC);
    const end = toUtcMs(segment.endDateUTC);
    const startOdometer = toNumber(segment.gpsOdometerMilesStart);
    const endOdometer = toNumber(segment.gpsOdometerMilesEnd);

    if (timeMs < start) return previousEnd ?? startOdometer ?? endOdometer;

    if (timeMs <= end) {
      if (startOdometer === null) return endOdometer ?? previousEnd;
      if (endOdometer === null || end <= start) return startOdometer;
      return startOdometer + (endOdometer - startOdometer) * ((timeMs - start) / (end - start));
    }

    previousEnd = endOdometer ?? startOdometer ?? previousEnd;
  }

  return previousEnd;
};

/** Drops pings with no timestamp or no odometer and puts the rest in time order. */
export const prepareGpsPings = (pings: GpsPing[] | undefined | null): GpsPing[] =>
  (pings || [])
    .filter((p) => p?.occurrenceDT && !isNaN(toUtcMs(p.occurrenceDT)) && toNumber(p.gpsOdometerMiles) !== null)
    .slice()
    .sort((a, b) => toUtcMs(a.occurrenceDT) - toUtcMs(b.occurrenceDT));

/**
 * Odometer from the last GPS ping at or before the given time.
 *
 * "At or before" rather than "nearest" deliberately: it's the convention
 * OrderApi already uses when it GPS-sources odometers, and it can never credit
 * an event with miles the vehicle hadn't driven yet.
 *
 * Returns null if there is no prior ping, or the nearest one is staler than
 * maxAgeMs — the caller falls back to segment interpolation.
 *
 * @param pings must already be through prepareGpsPings
 */
export const pingOdometerAt = (
  pings: GpsPing[],
  timeMs: number,
  maxAgeMs: number = MAX_PING_AGE_MS
): number | null => {
  if (!pings.length || isNaN(timeMs)) return null;

  // Binary search for the last ping at or before timeMs.
  let low = 0;
  let high = pings.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (toUtcMs(pings[mid].occurrenceDT) <= timeMs) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  if (found === -1) return null;
  if (timeMs - toUtcMs(pings[found].occurrenceDT) > maxAgeMs) return null;
  return toNumber(pings[found].gpsOdometerMiles);
};

/** Event type name, from typeId when the type list is supplied (a grid edit updates typeId, not type). */
const eventTypeName = (event: any, eventTypes?: { id: any; name?: string }[] | null): string | undefined =>
  eventTypes?.length && event?.typeId != null && event.typeId !== ""
    ? eventTypes.find((et) => String(et.id) === String(event.typeId))?.name
    : event?.type;

/**
 * Indexes of each non-Assist Wait End, mapped to its Wait Start's index.
 *
 * A pair is a Wait Start immediately followed by a Wait End among the active,
 * timed events in time order — the same adjacency the C# rules'
 * MovementInNonAssistWaitEvent check walks. Assist is the Wait Start's reason,
 * compared the way the grid does when it mirrors an odometer edit onto the pair.
 */
const nonAssistWaitEnds = (events: any[], eventTypes?: { id: any; name?: string }[] | null): Map<number, number> => {
  const ordered = events
    .map((event, index) => ({ event, index }))
    .filter(({ event }) => event?.isActive !== false && event?.time && !isNaN(toUtcMs(event.time)))
    .sort((a, b) => toUtcMs(a.event.time) - toUtcMs(b.event.time));

  const pairs = new Map<number, number>();
  for (let i = 0; i < ordered.length - 1; i++) {
    const start = ordered[i];
    const end = ordered[i + 1];
    if (
      eventTypeName(start.event, eventTypes) === "Wait Start" &&
      eventTypeName(end.event, eventTypes) === "Wait End" &&
      start.event.comments?.toLowerCase() !== "assist"
    ) {
      pairs.set(end.index, start.index);
    }
  }
  return pairs;
};

/**
 * Sets the odometer on every active event from GPS and recomputes the per-event
 * mileage. Deactivated rows are removed events — their history is left alone.
 *
 * A non-Assist wait is stationary by definition, so both halves of the pair take
 * the Wait Start's reading. Reading the Wait End at its own time would pick up
 * any drift across the stop and can cross a whole mile, which fails the
 * "Movement in non-Assist Wait event" rule. Assist waits keep a reading each.
 *
 * Mileage is recomputed the same way the API does on reload (this event's
 * odometer minus the previous event's) and chained in time order rather than
 * grid order, so retiming a row without dragging it doesn't show its mileage
 * against the wrong neighbour.
 *
 * @param eventTypes the event type list, so wait pairs are found by typeId; without
 *   it the event's `type` name is used
 */
export const applyGpsOdometers = (
  events: any[],
  rawSegments: GpsSegment[] | undefined | null,
  rawPings?: GpsPing[] | undefined | null,
  eventTypes?: { id: any; name?: string }[] | null
): ApplyGpsOdometersResult => {
  const segments = prepareGpsSegments(rawSegments);
  const pings = prepareGpsPings(rawPings);
  const empty = { events, updatedCount: 0, unmatchedCount: 0, fromPingCount: 0, interpolatedCount: 0 };
  if (!segments.length && !pings.length) return empty;

  let updatedCount = 0;
  let unmatchedCount = 0;
  let fromPingCount = 0;
  let interpolatedCount = 0;

  const sourceEvents = events || [];
  const waitEnds = nonAssistWaitEnds(sourceEvents, eventTypes);

  // Exact ping first; interpolate across the segment only if there isn't one.
  const readings = sourceEvents.map((event) => {
    if (event?.isActive === false || !event?.time) return null;
    const eventMs = toUtcMs(event.time);
    const fromPing = pingOdometerAt(pings, eventMs);
    if (fromPing !== null) return { odometer: fromPing, fromPing: true };
    const interpolated = gpsOdometerAt(segments, eventMs);
    return interpolated === null ? null : { odometer: interpolated, fromPing: false };
  });

  const updatedEvents = sourceEvents.map((event, index) => {
    if (event?.isActive === false) return event;
    if (!event?.time) {
      unmatchedCount++;
      return event;
    }

    // A non-Assist Wait End takes its Wait Start's reading. If the start had no
    // GPS, the end is left alone too rather than split from it.
    const waitStartIndex = waitEnds.get(index);
    const reading = waitStartIndex === undefined ? readings[index] : readings[waitStartIndex];

    if (reading === null) {
      unmatchedCount++;
      return event;
    }
    if (reading.fromPing) fromPingCount++;
    else interpolatedCount++;

    const truncated = truncateOdometer(reading.odometer);
    const current = toNumber(event.odometer);
    if (current !== null && truncateOdometer(current) === truncated) return event;

    updatedCount++;
    return { ...event, odometer: truncated };
  });

  const chainOrder = updatedEvents
    .map((event, index) => ({ event, index }))
    .filter(({ event }) => event?.time && !isNaN(toUtcMs(event.time)))
    .sort((a, b) => toUtcMs(a.event.time) - toUtcMs(b.event.time));

  const eventsWithMiles = [...updatedEvents];
  let previousOdometer: number | null = null;
  chainOrder.forEach(({ index }, position) => {
    const odometer = toNumber(eventsWithMiles[index].odometer);
    const miles =
      position === 0 || previousOdometer === null || odometer === null ? 0 : odometer - previousOdometer;
    eventsWithMiles[index] = { ...eventsWithMiles[index], eventDriveTimeMiles: miles };
    if (odometer !== null) previousOdometer = odometer;
  });

  return { events: eventsWithMiles, updatedCount, unmatchedCount, fromPingCount, interpolatedCount };
};
